/**
 * Catálogo de peças do processo do trabalho — dropdown do "Gerar rascunho".
 *
 * `skill` = skill CDRJ que o backend usa hoje. TODO(skills-por-peca): criar
 * skill específica para cada tipo de peça; enquanto isso, a maioria roteia
 * para a skill genérica `pecas-trabalhistas-cdrj` e o rótulo escolhido segue
 * no prompt (PECA_SOLICITADA.txt) para orientar a redação.
 */
export interface TipoPeca {
  /** slug estável (espelha backend/pecas/tipos_peca.py) — chave do modelo/rotas. */
  key: string;
  rotulo: string;
  skill: string;
  grupo: string;
}

const G1 = 'Fase de conhecimento';
const G2 = 'Fase recursal';
const G3 = 'Execução e liquidação';
const G4 = 'Incidentes e outras';

const GEN = 'pecas-trabalhistas-cdrj';

// Lista literal em ordem LÓGICA/cronológica (referência). O export exibido
// (TIPOS_PECA, abaixo) reordena alfabeticamente dentro de cada seção.
const TIPOS_PECA_RAW: TipoPeca[] = [
  // ── Fase de conhecimento ──
  { key: 'peticao-inicial',           rotulo: 'Reclamação Trabalhista (petição inicial)', skill: 'peticao-trabalhista-expert', grupo: G1 },
  { key: 'aditamento',                rotulo: 'Aditamento à petição inicial',             skill: GEN, grupo: G1 },
  { key: 'contestacao',               rotulo: 'Contestação',                              skill: GEN, grupo: G1 },
  { key: 'replica',                   rotulo: 'Réplica (impugnação à contestação)',       skill: 'replica-cdrj', grupo: G1 },
  { key: 'manif-documentos',          rotulo: 'Manifestação sobre documentos',            skill: GEN, grupo: G1 },
  { key: 'memoriais',                 rotulo: 'Memoriais (razões finais)',                skill: 'memoriais-cdrj', grupo: G1 },
  { key: 'manif-laudo',               rotulo: 'Manifestação sobre laudo pericial',        skill: 'esclarecimentos-periciais', grupo: G1 },
  { key: 'esclarecimentos-periciais', rotulo: 'Esclarecimentos periciais',                skill: 'esclarecimentos-periciais', grupo: G1 },
  { key: 'apresentacao-quesitos-conhecimento', rotulo: 'Apresentação de quesitos (perícia)', skill: 'esclarecimentos-periciais', grupo: G1 },
  { key: 'impugnacao-valor-causa',    rotulo: 'Impugnação ao valor da causa',             skill: GEN, grupo: G1 },
  { key: 'manif-generica',            rotulo: 'Manifestação genérica / Petição simples',  skill: GEN, grupo: G1 },

  // ── Fase recursal ──
  { key: 'recurso-ordinario',         rotulo: 'Recurso Ordinário (RO)',                   skill: 'recurso-ordinario-cdrj', grupo: G2 },
  { key: 'contrarrazoes-ro',          rotulo: 'Contrarrazões ao Recurso Ordinário',       skill: 'contrarrazoes-cdrj', grupo: G2 },
  { key: 'contraminuta-airo',         rotulo: 'Contraminuta ao AIRO',                     skill: 'contrarrazoes-cdrj', grupo: G2 },
  { key: 'embargos-declaracao',       rotulo: 'Embargos de Declaração (ED)',              skill: GEN, grupo: G2 },
  { key: 'contrarrazoes-ed',          rotulo: 'Contrarrazões aos Embargos de Declaração', skill: 'contrarrazoes-cdrj', grupo: G2 },
  { key: 'recurso-revista',           rotulo: 'Recurso de Revista (RR)',                  skill: GEN, grupo: G2 },
  { key: 'contrarrazoes-rr',          rotulo: 'Contrarrazões ao Recurso de Revista',      skill: 'contrarrazoes-cdrj', grupo: G2 },
  { key: 'airr',                      rotulo: 'Agravo de Instrumento em RR (AIRR)',       skill: GEN, grupo: G2 },
  { key: 'contraminuta-airr',         rotulo: 'Contraminuta ao AIRR',                     skill: 'contrarrazoes-cdrj', grupo: G2 },
  { key: 'agravo-interno',            rotulo: 'Agravo Interno / Regimental',              skill: GEN, grupo: G2 },
  { key: 'embargos-tst',              rotulo: 'Embargos ao TST (SDI)',                    skill: GEN, grupo: G2 },

  // ── Execução e liquidação ──
  { key: 'impugnacao-liquidacao',        rotulo: 'Impugnação à sentença de liquidação', skill: GEN, grupo: G3 },
  { key: 'embargos-execucao',            rotulo: 'Embargos à Execução',                  skill: GEN, grupo: G3 },
  { key: 'impugnacao-embargos-execucao', rotulo: 'Impugnação aos Embargos à Execução',   skill: GEN, grupo: G3 },
  { key: 'agravo-peticao',               rotulo: 'Agravo de Petição (AP)',               skill: GEN, grupo: G3 },
  { key: 'contraminuta-ap',              rotulo: 'Contraminuta ao Agravo de Petição',    skill: 'contrarrazoes-cdrj', grupo: G3 },
  { key: 'excecao-pre-executividade',    rotulo: 'Exceção de pré-executividade',         skill: GEN, grupo: G3 },
  { key: 'manif-calculos',               rotulo: 'Manifestação sobre cálculos',          skill: GEN, grupo: G3 },
  { key: 'apresentacao-quesitos-liquidacao', rotulo: 'Apresentação de quesitos (perícia)', skill: 'esclarecimentos-periciais', grupo: G3 },

  // ── Incidentes e outras ──
  { key: 'tutela-urgencia',             rotulo: 'Tutela de urgência (liminar)',  skill: GEN, grupo: G4 },
  { key: 'mandado-seguranca',           rotulo: 'Mandado de Segurança',          skill: GEN, grupo: G4 },
  { key: 'excecao-incompetencia',       rotulo: 'Exceção de incompetência',      skill: GEN, grupo: G4 },
  { key: 'impugnacao-justica-gratuita', rotulo: 'Impugnação à justiça gratuita', skill: GEN, grupo: G4 },
];

export const GRUPOS = [G1, G2, G3, G4];

/**
 * Catálogo EXIBIDO: agrupado por seção (na ordem de GRUPOS) e, DENTRO de cada
 * seção, em ordem ALFABÉTICA pelo rótulo (pt-BR, acento/caixa-insensível).
 */
export const TIPOS_PECA: TipoPeca[] = GRUPOS.flatMap((g) =>
  TIPOS_PECA_RAW.filter((t) => t.grupo === g).sort((a, b) =>
    a.rotulo.localeCompare(b.rotulo, 'pt-BR', { sensitivity: 'base' }),
  ),
);

/** Lookup por key (slug estável). */
export const TIPOS_PECA_BY_KEY: Record<string, TipoPeca> = Object.fromEntries(
  TIPOS_PECA.map((t) => [t.key, t]),
);

/** Sugestão automática pelo tipo_prazo do card (espelha o backend). */
export function sugestaoPorTipoPrazo(tipoPrazo?: string | null): TipoPeca {
  const mapa: Record<string, string> = {
    'Sentenca':              'Recurso Ordinário (RO)',
    'Acordao':               'Contrarrazões ao Recurso Ordinário',
    'Contrarrazoes':         'Contrarrazões ao Recurso Ordinário',
    'Juntada Parte Adversa': 'Réplica (impugnação à contestação)',
    'Audiencia Designada':   'Memoriais (razões finais)',
    'Petição Inicial':       'Reclamação Trabalhista (petição inicial)',
  };
  const rotulo = mapa[tipoPrazo ?? ''] ?? 'Manifestação genérica / Petição simples';
  return TIPOS_PECA.find((t) => t.rotulo === rotulo) ?? TIPOS_PECA[0];
}

/** Normaliza para busca acento-insensível. */
export function normalizar(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
